import React, { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import Confirmador from './components/ui/Confirmador'
import useAuthStore from './stores/authStore'
import { PageLoader } from './components/ui'
import { hasSection, isAdmin as isAdminUser, homePath } from './config/permissions'

// Layouts
import Layout from './components/layout/Layout'

// Pages (críticas, carga inmediata)
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Search from './pages/Search'
import Profile from './pages/Profile'
import BeneficiariosList from './pages/BeneficiariosList'

// Pages pesadas / secundarias (lazy-load → menor bundle inicial)
const Analytics = lazy(() => import('./pages/Analytics'))
const Users = lazy(() => import('./pages/Users'))
const Logs = lazy(() => import('./pages/Logs'))
const NewAssistance = lazy(() => import('./pages/NewAssistance'))
const AssistanceRegistration = lazy(() => import('./pages/AssistanceRegistration'))
const RegisterPerson = lazy(() => import('./pages/RegisterPerson'))
const DuplicateReview = lazy(() => import('./pages/DuplicateReview'))
const AdminAssistanceTypes = lazy(() => import('./pages/AdminAssistanceTypes'))
const CitizensList = lazy(() => import('./pages/CitizensList'))
const SoporteTecnico = lazy(() => import('./pages/SoporteTecnico'))
const ViajesCorrientes = lazy(() => import('./pages/ViajesCorrientes'))
const ViajesGoya = lazy(() => import('./pages/ViajesGoya'))
const ViajesCamioneta = lazy(() => import('./pages/ViajesCamioneta'))
// Colectivo y Camión comparten pagina: los dos van "a la zona" con destino variable.
const ViajesZona = lazy(() => import('./pages/ViajesZona'))
const ViajesPadron = lazy(() => import('./pages/ViajesPadron'))
const CajaCeliacos = lazy(() => import('./pages/CajaCeliacos'))
const Comedores = lazy(() => import('./pages/Comedores'))
const ComedoresAdministracion = lazy(() => import('./pages/ComedoresAdministracion'))
const Instituciones = lazy(() => import('./pages/Instituciones'))
const InstitucionProfile = lazy(() => import('./pages/InstitucionProfile'))
const Reclamos = lazy(() => import('./pages/Reclamos'))
const Capacitaciones = lazy(() => import('./pages/Capacitaciones'))
const Produccion = lazy(() => import('./pages/Produccion'))
const KitFemenino = lazy(() => import('./pages/KitFemenino'))
const OsintPersona = lazy(() => import('./pages/OsintPersona'))
const TarjetaSapucay = lazy(() => import('./pages/TarjetaSapucay'))
const TarjetaMamaMbarete = lazy(() => import('./pages/TarjetaMamaMbarete'))
const ValesRetiro = lazy(() => import('./pages/ValesRetiro'))
const ValeSeguimiento = lazy(() => import('./pages/ValeSeguimiento'))
const EvacuadosPlanilla = lazy(() => import('./pages/EvacuadosPlanilla'))
const MesaEntrada = lazy(() => import('./pages/MesaEntrada'))
const WhatsAppInbox = lazy(() => import('./pages/WhatsAppInbox'))
const Emprendedores = lazy(() => import('./pages/Emprendedores'))
const ElNino = lazy(() => import('./pages/ElNino'))
const Observatorio = lazy(() => import('./pages/observatorio/Observatorio'))

// Protected Route component
const ProtectedRoute = ({ children, requireAdmin = false, permission = null }) => {
  const { isAuthenticated, isLoading, user } = useAuthStore()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-social-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Gestión de usuarios: solo administradores.
  if (requireAdmin && !isAdminUser(user)) {
    return <Navigate to="/panel" replace />
  }

  // Secciones con permiso granular (admins siempre pasan).
  if (permission && !hasSection(user, permission)) {
    return <Navigate to="/panel" replace />
  }

  return children
}

// Public Route component (redirect if authenticated)
// Inicio según permisos: /buscar ya no es para todos.
const HomeRedirect = () => <Navigate to={homePath(useAuthStore((s) => s.user))} replace />

const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore()

  if (isAuthenticated) {
    return <Navigate to={homePath(user)} replace />
  }

  return children
}

function App() {
  return (
    <BrowserRouter>
      <Confirmador />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#262626',
            color: '#fff',
            borderRadius: '0.75rem',
            fontSize: '0.875rem',
            boxShadow: '0 10px 30px -12px rgba(15, 23, 42, 0.4)',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />

      <Routes>
        {/* Public routes */}
        <Route path="/vales/seguimiento/:token" element={<Suspense fallback={<PageLoader />}><ValeSeguimiento /></Suspense>} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomeRedirect />} />
          <Route path="panel" element={<Dashboard />} />
          <Route path="buscar" element={<ProtectedRoute permission="buscar"><Search /></ProtectedRoute>} />
          <Route path="persona/:id" element={<Profile />} />
          <Route path="osint/persona/:id" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><OsintPersona /></Suspense></ProtectedRoute>} />
          <Route path="analytics" element={<ProtectedRoute permission="analytics"><Suspense fallback={<PageLoader />}><Analytics /></Suspense></ProtectedRoute>} />
          <Route path="usuarios" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><Users /></Suspense></ProtectedRoute>} />
          <Route path="importar" element={<Navigate to="/panel" replace />} />
          <Route path="logs" element={<ProtectedRoute permission="logs"><Suspense fallback={<PageLoader />}><Logs /></Suspense></ProtectedRoute>} />
          <Route path="asistencia/nueva" element={<Suspense fallback={<PageLoader />}><NewAssistance /></Suspense>} />
          <Route path="asistencia/registrar" element={<Suspense fallback={<PageLoader />}><AssistanceRegistration /></Suspense>} />
          <Route path="personas/nueva" element={<Suspense fallback={<PageLoader />}><RegisterPerson /></Suspense>} />
          <Route path="revision-duplicados" element={<ProtectedRoute permission="revision_duplicados"><Suspense fallback={<PageLoader />}><DuplicateReview /></Suspense></ProtectedRoute>} />
          <Route path="admin/tipos-asistencia" element={<ProtectedRoute permission="tipos_asistencia"><Suspense fallback={<PageLoader />}><AdminAssistanceTypes /></Suspense></ProtectedRoute>} />
          <Route path="beneficiarios" element={<ProtectedRoute permission="beneficiarios"><BeneficiariosList /></ProtectedRoute>} />
          <Route path="ciudadanos" element={<ProtectedRoute permission="buscar"><Suspense fallback={<PageLoader />}><CitizensList /></Suspense></ProtectedRoute>} />
          <Route path="soporte" element={<Suspense fallback={<PageLoader />}><SoporteTecnico /></Suspense>} />
          <Route path="viajes-corrientes" element={<ProtectedRoute permission="viajes_corrientes"><Suspense fallback={<PageLoader />}><ViajesCorrientes /></Suspense></ProtectedRoute>} />
          <Route path="viajes-goya" element={<ProtectedRoute permission="viajes_goya"><Suspense fallback={<PageLoader />}><ViajesGoya /></Suspense></ProtectedRoute>} />
          <Route path="viajes-camioneta-mn" element={<ProtectedRoute permission="viajes_camioneta_mn"><Suspense fallback={<PageLoader />}><ViajesCamioneta vehicleKey="corrientes_camioneta_mn" vehicleName="Camioneta AF802MN" capacity={6} /></Suspense></ProtectedRoute>} />
          <Route path="viajes-camioneta-mo" element={<ProtectedRoute permission="viajes_camioneta_mo"><Suspense fallback={<PageLoader />}><ViajesCamioneta vehicleKey="corrientes_camioneta_mo" vehicleName="Camioneta AF802MO" capacity={6} /></Suspense></ProtectedRoute>} />
          <Route path="viajes-colectivo" element={<ProtectedRoute permission="viajes_colectivo"><Suspense fallback={<PageLoader />}><ViajesZona vehicleKey="colectivo" vehicleName="Colectivo" capacity={33} unidad="butacas" /></Suspense></ProtectedRoute>} />
          <Route path="viajes-camion" element={<ProtectedRoute permission="viajes_camion"><Suspense fallback={<PageLoader />}><ViajesZona vehicleKey="camion" vehicleName="Camión" capacity={30} unidad="espacios" /></Suspense></ProtectedRoute>} />
          <Route path="viajes-captiva" element={<ProtectedRoute permission="viajes_captiva"><Suspense fallback={<PageLoader />}><ViajesZona vehicleKey="captiva" vehicleName="Chevrolet Captiva" capacity={6} unidad="lugares" /></Suspense></ProtectedRoute>} />
          <Route path="viajes-padron" element={<ProtectedRoute permission="viajes_padron"><Suspense fallback={<PageLoader />}><ViajesPadron /></Suspense></ProtectedRoute>} />
          <Route path="caja-celiacos" element={<ProtectedRoute permission="caja_celiacos"><Suspense fallback={<PageLoader />}><CajaCeliacos /></Suspense></ProtectedRoute>} />
          <Route path="comedores" element={<ProtectedRoute permission="comedores"><Suspense fallback={<PageLoader />}><Comedores /></Suspense></ProtectedRoute>} />
          <Route path="capacitaciones" element={<ProtectedRoute permission="capacitaciones"><Suspense fallback={<PageLoader />}><Capacitaciones /></Suspense></ProtectedRoute>} />
          <Route path="kit-femenino" element={<ProtectedRoute permission="kit_femenino"><Suspense fallback={<PageLoader />}><KitFemenino /></Suspense></ProtectedRoute>} />
          <Route path="produccion" element={<ProtectedRoute permission="produccion"><Suspense fallback={<PageLoader />}><Produccion /></Suspense></ProtectedRoute>} />
          <Route path="reclamos" element={<ProtectedRoute permission="reclamos"><Suspense fallback={<PageLoader />}><Reclamos /></Suspense></ProtectedRoute>} />
          <Route path="instituciones" element={<ProtectedRoute permission="instituciones"><Suspense fallback={<PageLoader />}><Instituciones /></Suspense></ProtectedRoute>} />
          <Route path="institucion/:id" element={<ProtectedRoute permission="instituciones"><Suspense fallback={<PageLoader />}><InstitucionProfile /></Suspense></ProtectedRoute>} />
          <Route path="instituciones/:id" element={<ProtectedRoute permission="instituciones"><Suspense fallback={<PageLoader />}><InstitucionProfile /></Suspense></ProtectedRoute>} />
          <Route path="comedores-administracion" element={<ProtectedRoute permission="comedores_admin"><Suspense fallback={<PageLoader />}><ComedoresAdministracion /></Suspense></ProtectedRoute>} />
          <Route path="tarjeta-sapucay" element={<ProtectedRoute permission="tarjeta_sapucay"><Suspense fallback={<PageLoader />}><TarjetaSapucay /></Suspense></ProtectedRoute>} />
          <Route path="tarjeta-mama-mbarete" element={<ProtectedRoute permission="tarjeta_mama_mbarete"><Suspense fallback={<PageLoader />}><TarjetaMamaMbarete /></Suspense></ProtectedRoute>} />
          <Route path="vales" element={<ProtectedRoute permission="vales"><Suspense fallback={<PageLoader />}><ValesRetiro /></Suspense></ProtectedRoute>} />
          <Route path="inundaciones" element={<ProtectedRoute permission="inundaciones"><Suspense fallback={<PageLoader />}><EvacuadosPlanilla /></Suspense></ProtectedRoute>} />
          <Route path="el-nino" element={<ProtectedRoute permission="el_nino"><Suspense fallback={<PageLoader />}><ElNino /></Suspense></ProtectedRoute>} />
          <Route path="mesa-entrada" element={<ProtectedRoute permission="mesa_entrada"><Suspense fallback={<PageLoader />}><MesaEntrada /></Suspense></ProtectedRoute>} />
          <Route path="whatsapp" element={<ProtectedRoute permission="whatsapp"><Suspense fallback={<PageLoader />}><WhatsAppInbox /></Suspense></ProtectedRoute>} />
          <Route path="emprendedores" element={<ProtectedRoute permission="emprendedores"><Suspense fallback={<PageLoader />}><Emprendedores /></Suspense></ProtectedRoute>} />
          <Route path="observatorio" element={<ProtectedRoute permission="observatorio_ia" requireAdmin><Suspense fallback={<PageLoader />}><Observatorio /></Suspense></ProtectedRoute>} />
          {/* <Route path="osint" element={<Osint />} /> */}
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
